import React, { Dispatch, useState } from 'react'
import { Button } from '@lightspeed/design-system-react'
import { VendImage } from '@vend/react-utilities'
import { TerminalEventMonitoring } from '@lightspeed-payments/payments-terminal-sdk'
import { trackUserEvent } from '@vend/utilities'
import { useT } from '@transifex/react'
import { usePollRefund } from '../hooks/usePollRefund'
import { RefundEvent } from '../hooks/use-lspay-refund-server-reducer/types'
import { LSPAYModalContent } from '../../../modal-states/LSPAYModalContent'
import { Processing } from '../../../modal-states/Processing'
import { SwiperError, TerminalInfo } from '../../../../helpers/lspay.types'
import { cancelRefund } from '../../../../helpers/refund'

import { WEB_REGISTER } from '../../../../../../web-register.constant'
import { TAG, trackLSPayServerError } from '../../../../hooks/errorTracking'
import { useDispatchRefundEvent } from '../hooks/useDispatchRefundEvent'

interface PollRefundProps {
  dispatch: Dispatch<RefundEvent>
  isCardPresented?: boolean
  registerId: string
  registerSalePaymentId: string
  setupIntentId: string
  terminalMonitoringInstance: TerminalEventMonitoring | null
  terminalInfo: TerminalInfo
}

export const PollingRefund = ({
  dispatch,
  isCardPresented,
  registerId,
  registerSalePaymentId,
  setupIntentId,
  terminalMonitoringInstance,
  terminalInfo,
}: PollRefundProps) => {
  const cancelPolling = usePollRefund({
    dispatch,
    isCardPresented,
    registerId,
    registerSalePaymentId,
    setupIntentId,
    terminalMonitoringInstance,
    terminalInfo,
  })

  const [isCancelling, setIsCancelling] = useState(false)
  const { dispatchEvent } = useDispatchRefundEvent()
  const t = useT()

  const handleCancelRefund = async () => {
    // Track the event for clicking on the cancel refund button
    trackUserEvent(
      WEB_REGISTER.DISPLAY_NAME,
      'LSD clicked cancel refund button',
      {
        registerId,
        registerSalePaymentId,
        setupIntentId,
      }
    )

    setIsCancelling(true)

    cancelRefund({
      registerId,
      registerSalePaymentId,
      setupIntentId,
    })
      .then(response => {
        // cancel polling after getting the response from cancelRefund
        cancelPolling()

        dispatchEvent({
          dispatch,
          setupIntentId,
          terminalInfo,
          data: response,
          terminalMonitoringInstance,
        })
      })
      .catch((error: SwiperError) => {
        // track the error
        trackLSPayServerError(
          '[lspay-server-driven] failed to cancel refund',
          error,
          {
            registerId,
            registerSalePaymentId,
            setupIntentId,
          },
          TAG.refund
        )
        // absorb this error, there's nothing to do about it
      })
      .finally(() => {
        setIsCancelling(false)
      })
  }

  return (
    <LSPAYModalContent
      actions={
        !isCardPresented && (
          <Button
            variant="no"
            className="vd-mr2"
            loading={isCancelling}
            onClick={handleCancelRefund}
            data-track="cancel-refund-polling"
          >
            {t('Cancel Refund', {
              _context: 'Cancel refund button',
            })}
          </Button>
        )
      }
    >
      {isCardPresented ? (
        <Processing isRefund={true} />
      ) : (
        <>
          <VendImage
            relativeImagePath="payments/insert-card-v1.svg"
            relativeDarkImagePath="payments/insert-card-darkmode-v1.svg"
            className="vd-mb2"
          />

          <h2 className="vd-align-center">
            {t(`Tap, insert or swipe the customer's card`, {
              _context: 'Awaiting for card modal header',
            })}
          </h2>
          <div className="vd-align-center">
            <p>
              {t(
                'The customer card details will be securely stored only to process this refund.',
                {
                  _context: 'Awaiting card modal description for refund',
                }
              )}
              <br />
              {t(
                'Ensure the customer agrees for the card details to be stored.',
                {
                  _context: 'Awaiting card modal description for refund',
                }
              )}
            </p>
          </div>
        </>
      )}
    </LSPAYModalContent>
  )
}
